import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import debounce from "lodash.debounce";

const ZRPersonWorkAssigment = () => {
  const [query, setQuery] = useState("");
  const [year, setYear] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);

  const fetchResults = async (searchTerm, yearFilter) => {
    if (!searchTerm || searchTerm.length < 2) {
      setResults([]);
      setAvailableYears([]);
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const url = new URL("/api/search-persons-workassignment-zunftrat", window.location.origin);
      url.searchParams.append("q", searchTerm);
      if (yearFilter) url.searchParams.append("year", yearFilter);

      const response = await axios.get(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = (response.data || []).map(item => ({
        ...item,
        dates: Array.isArray(item.dates)
          ? item.dates
          : typeof item.dates === "string"
          ? item.dates.split(",").map(d => d.trim())
          : [],
      }));

      setResults(data);

      const years = Array.from(
        new Set(
          data.flatMap(item =>
            item.dates
              .map(dateStr => {
                const match = dateStr.match(/(\d{1,2})\.(\d{1,2})\.(\d{4})/);
                return match ? parseInt(match[3]) : null;
              })
              .filter(Boolean)
          )
        )
      ).sort((a, b) => b - a);

      setAvailableYears(years);
    } catch (error) {
      console.error("Fehler beim Abrufen der Personen:", error);
    } finally {
      setLoading(false);
    }
  };

  // debounce fetchResults, um API-Calls bei schnellem Tippen zu limitieren
  const debouncedFetchResults = useCallback(
    debounce((searchTerm, yearFilter) => {
      fetchResults(searchTerm, yearFilter);
    }, 500),
    []
  );

  // Suche bei Änderung von query oder year ausführen
  useEffect(() => {
    debouncedFetchResults(query, year);
  }, [query, year, debouncedFetchResults]);

  const handleQueryChange = (e) => {
    setQuery(e.target.value);
  };

  const handleYearChange = (e) => {
    setYear(e.target.value);
  };

  return (
    <div className="container mt-4">
      <h3 className="mb-4">🔍 Arbeitseinsätze einer Person suchen</h3>

      <div className="row mb-3">
        <div className="col-md-10 position-relative">
          <div className="input-group">
            <span className="input-group-text" id="search-addon">🔎</span>
            <input
              type="text"
              className="form-control"
              placeholder="Vor- oder Nachname eingeben..."
              value={query}
              onChange={handleQueryChange}
              aria-label="Name"
              aria-describedby="search-addon"
            />
          </div>
        </div>

        <div className="col-md-2">
          <select className="form-select" value={year} onChange={handleYearChange}>
            <option value="">Alle Jahre</option>
            {availableYears.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {loading && (
        <div className="mt-3 text-secondary">
          <div className="spinner-border spinner-border-sm me-2" role="status" />
          Suche läuft...
        </div>
      )}

      {!loading && query && results.length === 0 && (
        <div className="alert alert-warning mt-3" role="alert">
          Keine Einträge gefunden für <strong>{query}</strong>.
        </div>
      )}

      {results.length > 0 && (
        <div className="table-responsive mt-4">
          <table className="table table-hover table-bordered align-middle">
            <thead className="table-primary text-center">
              <tr>
                <th>Name</th>
                <th>Arbeitseinsatz</th>
                <th>Termine</th>
                <th>Status</th>
                <th>Kommentar</th>
              </tr>
            </thead>
            <tbody>
              {results.map((person, idx) => {
                let status = {
                  label: "—",
                  color: "secondary",
                  icon: "➖",
                };
                if (person.present) {
                  status = { label: "Anwesend", color: "success", icon: "✅" };
                } else if (person.excused) {
                  status = { label: "Entschuldigt", color: "info", icon: "📝" };
                } else if (person.unexcused) {
                  status = { label: "Unentschuldigt", color: "danger", icon: "❌" };
                }

                return (
                  <tr key={idx}>
                    <td>{person.memberName}</td>
                    <td>{person.assignmentName}</td>
                    <td>
                      {person.dates && Array.isArray(person.dates) && person.dates.length > 0 ? (
                        <ul className="mb-0">
                          {person.dates.map((d, i) => (
                            <li key={i}>{d}</li>
                          ))}
                        </ul>
                      ) : (
                        <em>Keine Termine</em>
                      )}
                    </td>
                    <td className="text-center text-nowrap">
                      <span className={`badge bg-${status.color}`}>
                        {status.icon} {status.label}
                      </span>
                    </td>
                    <td>{person.comment || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ZRPersonWorkAssigment;
